import React, { useState, useContext, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import clsx from "clsx";
import moment from "moment";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";

import MenuIcon from "@material-ui/icons/Menu";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import { Avatar as MuiAvatar } from "@material-ui/core";
import { SidebarSearchContext } from "../context/SidebarSearch";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import usePushNotifications from "../hooks/usePushNotifications";
import useCapacitorResume from "../hooks/useCapacitorResume";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { messages } from "../translate/languages";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BugReporterWidget from "../components/BugReporter/BugReporterWidget";

import { SocketContext } from "../context/Socket/SocketContext";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";
import useActivityPulse from "../hooks/useActivityPulse";

import ColorModeContext from "../layout/themeContext";

import NestedMenuItem from "material-ui-nested-menu-item";
import TrialGate from "../components/TrialGate";
import OnboardingTour from "../components/OnboardingTour";
import PlatformBanner from "../components/PlatformBanner";

// Voice lives at the AGENT-SESSION level (above any Ticket) so a live call
// survives ticket switching. Both providers are crash-isolated: on a render
// error they serve a disabled context and the CRM is unaffected.
import { SafeVoiceProvider } from "../context/Voice/VoiceContext";
import { SafeSoftphoneProvider } from "../context/Voice/SoftphoneContext";
import SoftphoneCallModal from "../components/SoftphoneCallModal";



const drawerWidth = 276;

// ───── Mobile design tokens (per /tmp/mobile-design-system.md A.2-A.6) ─────
// Single source of truth for spacing, touch targets, typography, radius,
// and the brand palette. DO NOT introduce values outside these tables —
// see Section E (anti-patterns) of the spec.
const SP = {
  xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48,
};
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
  display:    { fontSize: 24, lineHeight: 1.25, fontWeight: 700 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};

const useStyles = makeStyles((theme) => ({
  // Outer column wrapper so the trial banner can sit above the flex-row
  // layout (Drawer + AppBar + main) without being treated as a flex-item
  // of the row — that was pushing the sidebar into the middle of the
  // screen and leaving a huge empty `fancyBackground` gutter on the left.
  // `height: 100vh` (not minHeight) is critical: the drawer paper has no
  // explicit height, so without a hard cap on the row it grows to fit its
  // content and the inner `containerWithScroll` never overflows — i.e. the
  // sidebar loses its scrollbar. Capping the outer column locks the row,
  // which locks the drawer height, which restores sidebar scrolling.
  outerLayout: {
    display: "flex",
    flexDirection: "column",
    // Hard cap at 100dvh on ALL viewports so the inner chat container
    // (MessagesList with overflow:auto) gets a definite parent height.
    // Using 100dvh (dynamic viewport) instead of 100vh — dvh handles
    // iOS Safari toolbar shrinking correctly without breaking the chat
    // scroll chain. Previous xs relax to minHeight:100vh+overflow:visible
    // killed the chat container's internal scroll: messages stretched the
    // whole document and the input row floated below the fold. Tables on
    // mobile now reflow via cardStackMode v3 so they don't need
    // document-level scroll to work — keeping the hard cap is safe.
    // --app-vh is set by initKeyboardViewport to visualViewport.height while the
    // on-screen keyboard is up (iOS doesn't shrink 100dvh for the keyboard), so
    // the shell shrinks to the visible area and the bottom composer lands above
    // the keyboard. Unset otherwise → falls back to 100dvh (unchanged).
    height: "var(--app-vh, 100dvh)",
    overflow: "hidden",
    backgroundColor: theme.palette.fancyBackground,
  },
  // Mobile escape hatch for non-chat routes only. The hard 100dvh cap on
  // `outerLayout` is required for the chat scroll chain (MessagesList's
  // overflow:auto needs a definite parent height), but it broke document
  // scroll on ~25 other routes where the content legitimately exceeds the
  // viewport (Settings, Companies, Reports, etc.). Gating by isChatRoute
  // lets each side keep what it needs.
  outerLayoutScrollable: {
    // Non-chat routes are CAPPED on phones (inner `content` scrolls, document
    // does not). This is what keeps the position:fixed top bar from "catching a
    // ride": with document-scroll the bar dragged up/down on iOS; with the cap +
    // inner-scroll it STAYS PUT (user-confirmed). The notch offset is handled
    // separately on the toolbar.
    [theme.breakpoints.down("xs")]: {
      height: "var(--app-vh, 100dvh)",
      overflow: "hidden",
    },
  },
  root: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    // `position: relative` so AppBar (position: absolute) anchors to the
    // row container instead of <body>, keeping it neatly under the banner.
    position: "relative",
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.palette.primary,
      border: theme.mode === 'light' ? '1px solid rgba(0 124 102)' : '1px solid rgba(255, 255, 255, 0.5)',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.palette.primary,
    },
    direction: theme.direction,
    // Mobile responsive tables
    '& .MuiTableContainer-root': {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    '& .MuiTable-root': {
      [theme.breakpoints.down('sm')]: {
        minWidth: 650,
      },
    },
    // Responsive cards and grids
    '& .MuiCard-root': {
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(0.5),
      },
    },
  },
  // Mobile escape hatch for non-chat routes — paired with outerLayoutScrollable.
  // The default `root` uses overflow:hidden + minHeight:0 to keep the chat row
  // tight; on non-chat routes that traps long pages above the fold on phones.
  rootScrollable: {
    // Paired with outerLayoutScrollable: keep the row clipped on phones so the
    // inner `content` owns the scroll and the fixed top bar stays put.
    [theme.breakpoints.down("xs")]: {
      overflow: "hidden",
      minHeight: 0,
    },
  },
  avatar: {
    width: "100%",
  },
  toolbar: {
    paddingInlineEnd: 24,
    paddingInlineStart: 16,
    minHeight: 56,
    height: 56,
    color: theme.mode === "light" ? "#fff" : "rgba(255,255,255,0.92)",
    // Dark mode: subtle brand wash — dark base + brand-tinted gradient
    // that fades downward so the toolbar reads as "brand-kissed" without
    // becoming a banner. The previous full teal gradient was too loud;
    // the prior plain #131614 was too quiet (user couldn't see the
    // brand at all). This is the in-between.
    // Light mode: layered premium look — deeper base gradient with
    // a soft radial top-highlight + 1px glass-bevel inset. Reads
    // richer than the prior flat #25d366→#128c7e band and stops
    // looking like a "flat marketing strip".
    background: theme.mode === "light"
      ? `
          radial-gradient(ellipse 60% 100% at 0% 0%, rgba(255,255,255,0.18), transparent 60%),
          linear-gradient(135deg, #1ebe5d 0%, #128c7e 55%, #075e54 100%)
        `
      : "#131614",
    backgroundSize: "100% 100%",
    backgroundPosition: theme.direction === "rtl" ? "top right" : "top left",
    borderBottom: "none",
    boxShadow: theme.mode === "light"
      ? "inset 0 1px 0 rgba(255,255,255,0.22), 0 1px 0 rgba(7,94,84,0.4), 0 4px 16px rgba(7,94,84,0.18)"
      : "0 8px 24px rgba(0,0,0,0.4)",
    // Mobile: reduce padding, align items to inline-start — AND the notch fix.
    // The AppBar is position:fixed and the iOS WKWebView is full-bleed under the
    // status bar (viewport-fit=cover), so top:0 sits UNDER the notch and the icon
    // row collided with the system clock. Pad the toolbar down by
    // env(safe-area-inset-top) and grow its box by the same amount; boxSizing
    // border-box (explicit, not relying on a global reset) makes paddingTop eat
    // into the grown height, so the icon row stays 56px and sits in the bottom
    // 56px clear of the notch, while the green gradient fills the notch strip
    // (Telegram look). env()=0 on desktop/non-notch → inert. appBarSpacer is
    // grown by the same calc so content clears the bar.
    // IMPORTANT: this MUST stay the ONLY [breakpoints.down('sm')] key in this
    // object — a second one would overwrite it (JS object literal: last key wins,
    // full overwrite, not a merge). That collision silently dropped the notch
    // fix once already (review 2026-06-29).
    [theme.breakpoints.down('sm')]: {
      paddingInlineStart: 8,
      paddingInlineEnd: 8,
      justifyContent: 'flex-start',
      // Clean 56px bar. The WebView is inset below the status bar on this
      // surface (clock renders on the OS strip, web content starts under it),
      // so NO env(safe-area-inset-top) padding — adding it produced an empty
      // green band above the icons ("two-story"). The bar sits right under the
      // status strip; the pin (position:fixed) + inner-scroll keep it put.
      minHeight: 56,
      height: 56,
    },
  },
  // Mobile: burger sits at end of inline flow (visually right in LTR, left in RTL)
  // NOTE: a second `menuButton` definition follows below — the later one wins.
  // Keeping this block here for the mobile `order` override which gets merged in.
  // Mobile: icons container — push to opposite end via auto inline-start margin
  mobileIconsContainer: {
    display: 'contents', // Desktop: no effect on layout
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: 'auto',
      // Tablet (600–959px): all 5 action icons still render inline, so keep
      // the compact buttons that let the row fit.
      '& .MuiIconButton-root': { padding: 5 },
      '& .MuiSvgIcon-root': { fontSize: 22 },
    },
    // Phone (<600px): the declutter below leaves only the bell + kebab in
    // the bar, so the compact-cram override is no longer needed — restore
    // full ≥44px touch targets. Must come AFTER the down('sm') block so it
    // wins at phone widths (both media queries match there; source order
    // decides).
    [theme.breakpoints.down('xs')]: {
      '& .MuiIconButton-root': { padding: 10, minWidth: TT.min, minHeight: TT.min },
      '& .MuiSvgIcon-root': { fontSize: 22 },
    },
  },
  // Secondary toolbar actions (bug reporter / volume / announcements /
  // internal chat). Desktop + tablet: display:contents — the icons flow
  // inline in the toolbar exactly as before. Phone (<600px): they collapse
  // into a full-width shelf pinned under the 56px AppBar, toggled by the
  // kebab button. The shelf hides via visibility (NOT display:none) so the
  // children stay laid out — their self-anchored Popovers keep valid
  // getBoundingClientRect geometry even if one is open while the shelf
  // closes. visibility:hidden also drops them from tab order.
  secondaryIconShelf: {
    display: 'contents',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      position: 'fixed',
      top: 56,
      insetInline: 0,
      height: 56,
      padding: '0 8px',
      zIndex: theme.zIndex.drawer + 1,
      background: theme.mode === "light"
        ? "linear-gradient(135deg, #128c7e 0%, #075e54 100%)"
        : "#131614",
      borderTop: theme.mode === "light"
        ? "1px solid rgba(255,255,255,0.14)"
        : "1px solid rgba(255,255,255,0.08)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.22)",
      visibility: 'hidden',
      pointerEvents: 'none',
      // Room to breathe in the shelf — full 44px touch targets.
      '& .MuiIconButton-root': { padding: 10, minWidth: TT.min, minHeight: TT.min },
      '& .MuiSvgIcon-root': { fontSize: 24 },
    },
  },
  secondaryIconShelfOpen: {
    [theme.breakpoints.down('xs')]: {
      visibility: 'visible',
      pointerEvents: 'auto',
    },
  },
  // Kebab toggle for the shelf — phone only.
  moreButton: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'inline-flex',
      color: '#fff',
    },
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 56,
    minHeight: 56,
    flexShrink: 0,
    padding: "4px 12px",
    // Light mode: layered premium look — deeper base gradient with
    // a soft radial top-highlight + 1px glass-bevel inset. Reads
    // richer than the prior flat #25d366→#128c7e band and stops
    // looking like a "flat marketing strip".
    background: theme.mode === "light"
      ? `
          radial-gradient(ellipse 60% 100% at 0% 0%, rgba(255,255,255,0.18), transparent 60%),
          linear-gradient(135deg, #1ebe5d 0%, #128c7e 55%, #075e54 100%)
        `
      : "#131614",
    backgroundSize: "100% 100%",
    backgroundPosition: theme.direction === "rtl" ? "top right" : "top left",
    borderBottom: "none",
    boxShadow: theme.mode === "light"
      ? "inset 0 1px 0 rgba(255,255,255,0.22), 0 1px 0 rgba(7,94,84,0.4), 0 4px 16px rgba(7,94,84,0.18)"
      : "0 8px 24px rgba(0,0,0,0.4)",
    "& .MuiIconButton-root": {
      color: "#fff",
      padding: 6,
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.15)",
      },
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    boxShadow: "none",
    borderBottom: "none",
    // Mobile: pin the top bar to the viewport so it STAYS under the notch when
    // the page scrolls (native-app feel, like Telegram). The shell is capped at
    // 100dvh on ALL mobile routes now (outerLayoutScrollable no longer relaxes
    // non-chat routes to document-scroll), so the inner `content` (overflow:auto)
    // scrolls and the document does not — that's what keeps this fixed bar from
    // riding up/down on iOS. The existing appBarSpacer (56px, in
    // <main>) already reserves the content offset, so fixed doesn't overlap
    // content. !important beats MUI's position="absolute"
    // (.MuiAppBar-positionAbsolute); scoped to <sm so desktop is unchanged.
    // DO NOT re-add document-scroll on non-chat mobile — it brings the ride back.
    [theme.breakpoints.down("sm")]: {
      position: "fixed !important",
      top: 0,
      left: 0,
      right: 0,
      width: "100%",
    },
  },
  appBarShift: {
    marginInlineStart: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  menuButton: {
    marginInlineEnd: 24,
    padding: 8,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.1)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    },
    "& .MuiSvgIcon-root": {
      color: "#fff",
    },
    // Mobile tap target ≥44 px (TT.min) per A.3.
    [theme.breakpoints.down("xs")]: {
      minHeight: TT.min,
      minWidth: TT.min,
      marginInlineEnd: SP.xs,
    },
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    textAlign: "start",
    marginInlineStart: 15,
    flexGrow: 1,
    fontSize: 14,
    fontWeight: 500,
    color: "rgba(255,255,255,0.95)",
    letterSpacing: "0.2px",
    "& b": {
      fontWeight: 700,
      color: "#fff",
    },
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowY: "clip",
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#131614",
    borderInlineEnd: theme.mode === "light"
      ? "1px solid rgba(0,0,0,0.06)"
      : "1px solid rgba(255,255,255,0.06)",
    ...theme.scrollbarStylesSoft,
    [theme.breakpoints.down('sm')]: {
      position: "fixed",
      zIndex: theme.zIndex.drawer + 2,
      // 100dvh (not 100vh): on iOS 100vh is the LARGEST viewport and exceeds
      // the visible area, so the drawer ran taller than the screen and its
      // bottom nav items (Settings…) sat off-screen, unreachable by scroll.
      // dvh matches the actual visible height so navScroll can reach the end.
      height: "100dvh",
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "clip",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(9),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    // Was breakpoints.down('sm') (=959 px) which mismatched the JS
    // `innerWidth < 600` check that flips drawerVariant. In the
    // 600-959 range the variant stays "permanent" but the paper was
    // being styled as zero-width + display:none, so the toolbar got
    // no left margin and the user had to use the hamburger anyway.
    // Aligning to xs (=599 px) matches the JS source of truth.
    [theme.breakpoints.down('xs')]: {
      width: 0,
      display: "none",
    },
    // When collapsed: hide labels, center icons, tighten row.
    // Wider listItem (margin/padding 12px each + 32px icon chip
    // + 14px label) used to spill its first character into the
    // page when the drawer crunches down to 72px wide.
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiList-root .MuiListItem-root": {
      margin: "4px",
      padding: "0",
      justifyContent: "center",
    },
    "& .MuiListItem-root .MuiListItemIcon-root": {
      minWidth: 0,
      justifyContent: "center",
    },
    // Hide the section subheaders too — without labels they
    // become noisy hairlines next to the icons.
    "& .MuiListSubheader-root": {
      display: "none",
    },
  },
  appBarSpacer: {
    // Matches the toolbar's 56px height so content starts just below the fixed
    // AppBar (no env padding — the WebView is already inset below the status bar).
    minHeight: "56px",
  },
  // Soft route-level fade — every navigation replays this so the
  // content area drifts in instead of snapping. 220ms is the sweet
  // spot: long enough to read as motion, short enough not to feel
  // sluggish on quick taps.
  "@keyframes routeFade": {
    "0%": { opacity: 0, transform: "translateY(6px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  routeFade: {
    animation: "$routeFade 220ms ease-out",
    willChange: "opacity, transform",
    // Must fill the main flex column so children using
    // `height: 100%` (TicketsCustom chatContainer in particular)
    // get the right computed height. Without this the wrapper
    // collapses and the chat area sits flush to the toolbar.
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flex: 1,
    overflow: "auto",
    // Prevents flicking past the top/bottom edge of this primary scroll
    // surface from rubber-banding/scroll-chaining into the outer document
    // (this container doesn't spread theme.scrollbarStyles, so it needs its
    // own overscroll containment). WebkitOverflowScrolling is a legacy iOS
    // momentum-scroll fallback.
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    boxSizing: "border-box",
    [theme.breakpoints.down('sm')]: {
      marginInlineStart: 0,
      padding: theme.spacing(1),
    },
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column"
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    overflowX: "clip",
    ...theme.scrollbarStyles,
  },

  // ───── Premium drawer chrome (Linear / Stripe pattern) ─────
  drawerBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
  },
  brandHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 64,
    padding: "0 16px",
    flexShrink: 0,
    boxSizing: "border-box",
    borderBottom: theme.palette.type === "light"
      ? "1px solid rgba(15, 23, 42, 0.06)"
      : "1px solid rgba(255,255,255,0.06)",
    background: theme.palette.type === "light"
      ? "linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)",
  },
  brandLogoBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  brandLogoImg: {
    width: 32,
    height: 32,
    borderRadius: 8,
    objectFit: "contain",
    // White chip so EVERY tenant's logo renders cleanly. Was a hardcoded
    // Whapiz-green gradient (wrong for white-label) on an <img> with no src —
    // which showed an empty green box with a broken-image icon.
    background: "#fff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(15,23,42,0.08)",
    boxShadow: theme.palette.type === "dark"
      ? "0 2px 8px rgba(0,0,0,0.40)"
      : "0 2px 8px rgba(15,23,42,0.10)",
    padding: 4,
  },
  brandText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  brandName: {
    // Roboto-only per Raviv 2026-05-19 — no Fraunces / Frank Ruhl / Manrope
    // loaded. Use a heavier weight to keep the wordmark stand-out.
    fontFamily: `'Roboto',system-ui,-apple-system,sans-serif`,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "-0.014em",
    color: theme.palette.type === "light" ? "#0a2620" : "#e8efd8",
    lineHeight: 1.1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  brandRole: {
    // Roboto-only per Raviv 2026-05-19.
    fontFamily: `'Roboto',system-ui,-apple-system,sans-serif`,
    fontSize: 10,
    fontWeight: 700,
    color: theme.palette.type === "light" ? COL.brandGreenDeep : "#2ee37a",
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    marginTop: 4,
  },
  brandCollapseBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    color: theme.palette.type === "light" ? "#64748b" : "rgba(255,255,255,0.65)",
    "&:hover": {
      backgroundColor: theme.palette.type === "light"
        ? "rgba(15, 23, 42, 0.05)"
        : "rgba(255,255,255,0.06)",
    },
    "& .MuiSvgIcon-root": { fontSize: 18 },
    // Android audit 2026-06-30: this is the drawer-collapse chevron a PHONE user
    // actually taps (the open temporary drawer). 30px is below the 44px touch
    // minimum — enlarge to 44px on phones only (down('xs') = the 600px JS cutover
    // this layout uses); the ≥600px permanent rail keeps the compact 30px.
    [theme.breakpoints.down("xs")]: {
      width: 44,
      height: 44,
    },
  },

  searchWrap: {
    padding: "10px 12px 6px",
    flexShrink: 0,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 36,
    padding: "0 10px",
    borderRadius: 10,
    boxSizing: "border-box",
    background: theme.palette.type === "light"
      ? "#f1f5f9"
      : "rgba(255,255,255,0.04)",
    border: theme.palette.type === "light"
      ? "1px solid #eef0f5"
      : "1px solid rgba(255,255,255,0.06)",
    transition: "border-color .15s ease, background .15s ease",
    "&:focus-within": {
      borderColor: "#86efac",
      background: theme.palette.type === "light"
        ? "#fff"
        : "rgba(37, 211, 102, 0.08)",
    },
    "& input": {
      flex: 1,
      border: "none",
      outline: "none",
      fontSize: 16,
      fontWeight: 500,
      color: theme.palette.type === "light" ? "#0f172a" : "#f1f5f9",
      background: "transparent",
      padding: 0,
      "&::placeholder": {
        color: theme.palette.type === "light" ? "#94a3b8" : "rgba(255,255,255,0.45)",
      },
    },
  },
  searchKbd: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10.5,
    fontWeight: 700,
    color: theme.palette.type === "light" ? "#64748b" : "rgba(255,255,255,0.55)",
    background: theme.palette.type === "light" ? "#fff" : "rgba(255,255,255,0.06)",
    border: theme.palette.type === "light"
      ? "1px solid #e2e8f0"
      : "1px solid rgba(255,255,255,0.08)",
    borderRadius: 6,
    padding: "1px 6px",
    lineHeight: 1.4,
  },
  searchIconStyled: {
    fontSize: 16,
    color: theme.palette.type === "light" ? "#94a3b8" : "rgba(255,255,255,0.5)",
  },

  navScroll: {
    flex: 1,
    minHeight: 0,
    // Bottom inset clears the iOS home indicator so the last nav item
    // (Settings) is fully reachable, not tucked under the gesture bar.
    padding: "4px 0 calc(8px + env(safe-area-inset-bottom, 0px))",
    overflowY: "auto",
    overflowX: "clip",
    WebkitOverflowScrolling: "touch",
    ...theme.scrollbarStyles,
  },

  userCard: {
    flexShrink: 0,
    margin: "8px 12px 12px",
    padding: "10px 12px",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    boxSizing: "border-box",
    background: theme.palette.type === "light"
      ? "#f8fafc"
      : "rgba(255,255,255,0.04)",
    border: theme.palette.type === "light"
      ? "1px solid #eef0f5"
      : "1px solid rgba(255,255,255,0.06)",
    cursor: "pointer",
    transition: "border-color .15s ease, background .15s ease",
    "&:hover": {
      borderColor: "#86efac",
      background: theme.palette.type === "light"
        ? "#fff"
        : "rgba(37, 211, 102, 0.08)",
    },
  },
  // ───── Toolbar variant of the user pill ─────
  // Same avatar + name + role pattern as the sidebar card, but
  // styled to sit inside the top AppBar. Light theme runs on the
  // green gradient toolbar so the text is white; dark theme sits
  // on #131614 surface with the regular dark palette.
  toolbarUser: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    paddingBlock: 4,
    paddingInlineStart: 4,
    paddingInlineEnd: 10,
    borderRadius: RAD.pill,
    cursor: "pointer",
    marginInlineStart: 8,
    transition: "background-color 160ms ease",
    "&:hover": {
      backgroundColor: theme.mode === "light"
        ? "rgba(255,255,255,0.16)"
        : "rgba(255,255,255,0.06)",
    },
    // Mobile tap target ≥44 px (TT.min) per A.3 — the pill collapses to
    // avatar-only on xs (toolbarUserBody is display:none below), so make
    // sure the remaining hitbox is still ≥44 px square.
    [theme.breakpoints.down("xs")]: {
      minHeight: TT.min,
      minWidth: TT.min,
      padding: 4,
      justifyContent: "center",
    },
  },
  toolbarUserAvatar: {
    width: 32,
    height: 32,
    fontSize: 13,
    fontWeight: 700,
    background: theme.mode === "light"
      ? "rgba(255,255,255,0.95)"
      : "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    color: theme.mode === "light" ? "#128c7e" : "#fff",
    boxShadow: theme.mode === "light"
      ? "0 2px 8px rgba(0,0,0,0.12)"
      : "0 4px 12px rgba(37, 211, 102, 0.32)",
    flexShrink: 0,
  },
  toolbarUserBody: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    lineHeight: 1.15,
    // Phone: hide the name + role text so only the avatar shows in the
    // already-crowded mobile toolbar (hamburger + 4 popovers + avatar).
    // The same identity menu still opens from the avatar pill, just
    // without the secondary text that pushed the toolbar past 360 px.
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  toolbarUserName: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.mode === "light" ? "#fff" : "#f1f5f9",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 180,
  },
  toolbarUserRole: {
    fontSize: 10,
    fontWeight: 700,
    color: theme.mode === "light"
      ? "rgba(255,255,255,0.78)"
      : "rgba(255,255,255,0.55)",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginTop: 1,
  },
  toolbarUserOnlineDot: {
    position: "absolute",
    insetInlineEnd: 0,
    bottom: 0,
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#22c55e",
    border: theme.mode === "light"
      ? "2px solid #16a34a"
      : "2px solid #131614",
  },
  userCardAvatar: {
    width: 34,
    height: 34,
    fontSize: 13,
    fontWeight: 700,
    background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(37, 211, 102, 0.32)",
    flexShrink: 0,
  },
  userCardOnlineDot: {
    position: "absolute",
    insetInlineEnd: 0,
    bottom: 0,
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#22c55e",
    border: theme.palette.type === "light"
      ? "2px solid #fff"
      : "2px solid #131614",
  },
  userCardBody: {
    flex: 1,
    minWidth: 0,
  },
  userCardName: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.palette.type === "light" ? "#0f172a" : "#f1f5f9",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
  },
  userCardRole: {
    fontSize: 10.5,
    fontWeight: 600,
    color: theme.palette.type === "light" ? "#64748b" : "rgba(255,255,255,0.55)",
    marginTop: 2,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  userCardKbd: {
    color: theme.palette.type === "light" ? "#94a3b8" : "rgba(255,255,255,0.45)",
    "& .MuiSvgIcon-root": { fontSize: 16 },
  },
  NotificationsPopOver: {
    // color: theme.barraSuperior.secondary.main,
  },
  toolbarIconButton: {
    padding: 8,
    marginInlineStart: 4,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.08)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
      transform: "translateY(-1px)",
    },
    "& .MuiSvgIcon-root": {
      color: "rgba(255,255,255,0.9)",
      fontSize: 22,
    },
    // Mobile tap target ≥44 px (TT.min) per A.3 — the input zone, not just
    // the visual icon. MUI IconButton "small" sits at ~30 px otherwise.
    [theme.breakpoints.down("xs")]: {
      minHeight: TT.min,
      minWidth: TT.min,
      padding: 10,
    },
  },
  userMenuButton: {
    padding: 6,
    marginInlineStart: 8,
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.12)",
    border: "2px solid rgba(255,255,255,0.2)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      borderColor: "rgba(255,255,255,0.4)",
    },
    "& .MuiSvgIcon-root": {
      color: "#fff",
      fontSize: 26,
    },
    // Mobile tap target ≥44 px (TT.min) per A.3.
    [theme.breakpoints.down("xs")]: {
      minHeight: TT.min,
      minWidth: TT.min,
    },
  },
  logo: {
    width: "auto",
    maxWidth: "200px",
    height: "50px",
    objectFit: "contain",
    content: "url(" + theme.calculatedLogoDark() + ")",
    marginTop: "8px",
    marginBottom: "8px",
  },
  hideLogo: {
    display: "none",
  },
  smallLogo: {
    width: "36px",
    height: "36px",
    objectFit: "contain",
    content: "url(" + theme.calculatedLogoDark() + ")",
    borderRadius: "8px",
  },
hideOnMobile: {
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
},
}));

const LoggedInLayout = ({ children, themeToggle }) => {
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  // Chat routes own their own internal scroll (MessagesList overflow:auto
  // inside a 100dvh-capped column). Everywhere else needs the document to
  // scroll on mobile so long pages like Settings or Reports aren't trapped
  // above the fold. Includes the popover ChatPopover route under /chats.
  const isChatRoute = /^\/(tickets|chats)/.test(location.pathname);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Phone-only: the kebab-toggled shelf holding the secondary toolbar icons.
  const [mobileShelfOpen, setMobileShelfOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [sidebarSearch, setSidebarSearch] = useState("");
  // const [dueDate, setDueDate] = useState("");
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  // noSsr: true — default noSsr=false returns false on first render and
  // re-evaluates post-commit, so super users on >=600px viewports first
  // painted the icon-only back-to-admin control then swapped to the
  // labelled Button — a one-frame flash + toolbar reflow on every load.
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  // Phone = below the app's real mobile cutover (600px). Same source as
  // greaterThenSm so both flip together on resize.
  const isPhone = !greaterThenSm;

  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  // Activity pulse: tracks user interactions and sends heartbeat to backend
  // every 60 s so UserActivityDrawer shows real active/idle/action metrics.
  useActivityPulse(!!user?.id);

  // Native push registration (Capacitor iOS/Android only — no-op on web).
  // Lives here, inside the authenticated shell, so the iOS permission prompt
  // can only appear after login — never on the launch/login screen
  // (Apple Guideline 2.1). The hook itself also gates on user?.id.
  usePushNotifications(user, history);

  // Native foreground catch-up (Capacitor iOS/Android only — no-op on web).
  // On app resume: forceReconnect the socket + invalidate the tickets query so
  // the list isn't stale until a full close+reopen. See useCapacitorResume.
  useCapacitorResume();

  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem("volume");
    return savedVolume !== null ? parseFloat(savedVolume) : 1;
  });

  const { dateToClient } = useDate();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    const updateDrawerVariant = () => {
      if (window.innerWidth < 600) {
        setDrawerVariant("temporary");
      } else {
        setDrawerVariant("permanent");
      }
    };

    // Run once on mount and on every resize / orientation change
    updateDrawerVariant();
    window.addEventListener("resize", updateDrawerVariant);
    return () => window.removeEventListener("resize", updateDrawerVariant);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket connection for real-time user status updates
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");

    if (!companyId) return;

    const socket = socketManager.GetSocket(companyId);

    // Emit user status on connect and periodically — keeps the
    // online/offline indicator accurate elsewhere in the app even
    // though we no longer render a per-user greeting in the toolbar.
    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleCloseProfileMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleCloseLanguageMenu = () => {
    setAnchorEl(null);
    setLanguageOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseProfileMenu();
  };


  const handleClickLogout = () => {
    handleCloseProfileMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }

  const handleChooseLanguage = (language) => {
    localStorage.setItem("language",language);
    i18n.changeLanguage(language);
    window.location.reload();
  }

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <SafeVoiceProvider>
    <SafeSoftphoneProvider>
    <div className={clsx(classes.outerLayout, !isChatRoute && classes.outerLayoutScrollable)}>
      <TrialGate />
      <div className={clsx(classes.root, !isChatRoute && classes.rootScrollable)}>
      <OnboardingTour />
      {/* The temporary Drawer (variant="temporary" on mobile) already renders
          its own Modal backdrop with a built-in fade transition and
          click-to-dismiss (onClose below) — the custom hard-pop overlay that
          used to live here duplicated it without any transition, so it was
          removed in favor of MUI's own animated backdrop. */}
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {drawerOpen ? (
          <div className={classes.drawerBody}>
            {/* Brand zone — logo medallion + company name + role +
                collapse chevron. Premium SaaS sidebars start here. */}
            <div className={classes.brandHeader}>
              <div className={classes.brandLogoBox}>
                <img className={classes.brandLogoImg} src={theme.calculatedLogoLight()} alt="logo" />
                <div className={classes.brandText}>
                  <span className={classes.brandName}>
                    {user?.company?.name || theme.appName || "Whapiz"}
                  </span>
                  <span className={classes.brandRole}>
                    {user?.profile === "admin"
                      ? i18n.t("mainDrawer.workspace.admin", { defaultValue: "Admin workspace" })
                      : user?.profile === "supervisor"
                      ? i18n.t("mainDrawer.workspace.supervisor", { defaultValue: "Supervisor workspace" })
                      : i18n.t("mainDrawer.workspace.default", { defaultValue: "Workspace" })}
                  </span>
                </div>
              </div>
              <IconButton
                size="small"
                className={classes.brandCollapseBtn}
                onClick={() => setDrawerOpen(false)}
                aria-label="collapse sidebar"
              >
                {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </div>

            {/* Search — filters MainListItems via context. ⌘K hint
                is decorative for now (no global palette wired up). */}
            <div className={classes.searchWrap}>
              <div className={classes.searchBox}>
                <SearchRoundedIcon className={classes.searchIconStyled} />
                <input
                  type="text"
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  placeholder={i18n.t("mainDrawer.search", { defaultValue: "חיפוש בתפריט..." })}
                />
                <span className={classes.searchKbd}>⌘K</span>
              </div>
            </div>

            {/* Scrollable nav body — the existing menu, only the
                shell around it changed. */}
            <div className={classes.navScroll}>
              <SidebarSearchContext.Provider value={sidebarSearch}>
                <List style={{ padding: 0 }}>
                  <MainListItems
                    drawerClose={drawerClose}
                    drawerOpen={drawerOpen}
                    collapsed={!drawerOpen}
                  />
                </List>
              </SidebarSearchContext.Provider>
            </div>

            {/* User card was previously pinned at the bottom of the
                sidebar with a three-dot more icon. Per Raviv it now
                lives in the top toolbar (toolbarUser) so the identity
                affordance is the same on every page regardless of
                whether the sidebar is open or collapsed. */}
          </div>
        ) : (
          <>
            <div className={classes.toolbarIcon}>
              <img className={drawerOpen ? classes.logo : classes.hideLogo} alt="logo" />
              <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
                <ChevronLeftIcon />
              </IconButton>
            </div>
            <List className={classes.containerWithScroll}>
              <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} collapsed={!drawerOpen} />
            </List>
            <Divider />
          </>
        )}
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            variant="contained"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>

          {/* Spacer pushes the toolbar icons to the far end of the
              bar (left edge in RTL, right in LTR). Used to be the
              greeting Typography that grew to fill — without a
              spacer the icons collapse next to the burger menu. */}
          <div style={{ flexGrow: 1 }} />

          {/* Shadow-mode indicator (approved AI-OS design). Scoped to the
              AI-OS routes on purpose: the Brain observes and advises there,
              so the promise belongs on that screen — the rest of the CRM
              top bar is untouched. Hidden on phones, where the bar is
              already crowded. */}
          {greaterThenSm && location.pathname.startsWith("/ai-os") && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 13px",
                marginInlineEnd: 8,
                background: "rgba(0,0,0,0.18)",
                borderRadius: 999,
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#a7f3d0",
                  flex: "none",
                }}
              />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#fff" }}>
                {i18n.t("mainDrawer.appBar.shadowMode", {
                  defaultValue: "מצב צל — צופה ומייעץ",
                })}
              </span>
            </div>
          )}

          {/* Back-to-admin shortcut — super users only. Per Raviv 2026-06-03:
              visible AppBar button (not only avatar dropdown) so super-admin
              who deep-linked into a tenant CRM can bounce straight back to
              /admin without opening the profile menu. The avatar dropdown
              MenuItem below stays as a redundant access point.

              Mobile (<600px): collapses to icon-only IconButton — topbar is
              already cramped on mobile (avatar + 4 icons + hamburger) so the
              labelled variant would overflow. Tooltip + aria-label carry text. */}
          {user?.super === true && (
            !greaterThenSm ? (
              <Tooltip
                title={i18n.t("mainDrawer.appBar.backToAdmin", {
                  defaultValue: "Back to admin",
                })}
              >
                <IconButton
                  size="small"
                  onClick={() => history.push("/admin")}
                  style={{
                    marginInlineEnd: 4,
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.4)",
                    // ≥44px tap target — the declutter freed up row width,
                    // so the shield no longer needs to stay undersized.
                    padding: 10,
                    minWidth: TT.min,
                    minHeight: TT.min,
                  }}
                  aria-label={i18n.t("mainDrawer.appBar.backToAdmin", {
                    defaultValue: "Back to admin",
                  })}
                >
                  <VerifiedUserOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VerifiedUserOutlinedIcon />}
                onClick={() => history.push("/admin")}
                style={{
                  marginInlineEnd: 8,
                  borderColor: "rgba(255,255,255,0.4)",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                }}
                aria-label={i18n.t("mainDrawer.appBar.backToAdmin", {
                  defaultValue: "Back to admin",
                })}
              >
                {i18n.t("mainDrawer.appBar.backToAdmin", {
                  defaultValue: "Back to admin",
                })}
              </Button>
            )
          )}

          {/* Icons container - pushed left on mobile */}
          <div className={classes.mobileIconsContainer}>
            {isPhone ? (
              // ── Phone (<600px): declutter ──────────────────────────────
              // The bell stays inline (its unread badge must be glanceable);
              // the other four actions collapse into a kebab-toggled shelf
              // pinned under the 56px bar. Rendering this branch only on phone
              // keeps the desktop/tablet DOM below byte-identical to before.
              <>
                <div
                  className={clsx(
                    classes.secondaryIconShelf,
                    mobileShelfOpen && classes.secondaryIconShelfOpen
                  )}
                >
                  <BugReporterWidget />
                  <NotificationsVolume setVolume={setVolume} volume={volume} />
                  <AnnouncementsPopover />
                  <ChatPopover />
                </div>

                {user.id && <NotificationsPopOver volume={volume} />}

                <IconButton
                  className={classes.moreButton}
                  onClick={() => setMobileShelfOpen((open) => !open)}
                  aria-expanded={mobileShelfOpen}
                  aria-label={i18n.t("mainDrawer.appBar.moreActions", {
                    defaultValue: "More actions",
                  })}
                >
                  {mobileShelfOpen ? <CloseIcon /> : <MoreVertIcon />}
                </IconButton>
              </>
            ) : (
              // ── Desktop + tablet: unchanged inline icon cluster ────────
              <>
                {/* Bug Reporter trigger — lives in the AppBar (white icon on the
                    green bar) rather than floating in a strip below it, per Raviv.
                    The widget also renders its (portaled) dialog + status snackbar. */}
                <BugReporterWidget />

                <NotificationsVolume
                  setVolume={setVolume}
                  volume={volume}
                />

                {user.id && <NotificationsPopOver volume={volume} />}

                <AnnouncementsPopover />

                <ChatPopover />
              </>
            )}
          </div>

          {/* User pill — moved from the sidebar bottom up here so the
              identity affordance is always in the same place across
              all screen sizes. Clicking opens the same profile /
              language / logout menu that the sidebar card used. */}
          {user.id && (
            <div
              className={classes.toolbarUser}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setMenuOpen(true);
              }}
            >
              <div style={{ position: "relative" }}>
                <MuiAvatar className={classes.toolbarUserAvatar}>
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </MuiAvatar>
                <span className={classes.toolbarUserOnlineDot} />
              </div>
              <div className={classes.toolbarUserBody}>
                <span className={classes.toolbarUserName}>{user?.name || "—"}</span>
                <span className={classes.toolbarUserRole}>
                  {user?.profile === "admin"
                    ? i18n.t("mainDrawer.role.administrator", { defaultValue: "Administrator" })
                    : user?.profile === "supervisor"
                    ? i18n.t("mainDrawer.role.supervisor", { defaultValue: "Supervisor" })
                    : i18n.t("mainDrawer.role.agent", { defaultValue: "Agent" })}
                </span>
              </div>
            </div>
          )}

          <div>
            <Menu
              id="language-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={languageOpen}
              onClose={handleCloseLanguageMenu}
            >
            {
              Object.keys(messages).map((m) => (
                <MenuItem key={m} onClick={() => handleChooseLanguage(m)}>
                  {messages[m].translations.mainDrawer.appBar.i18n.language}
                </MenuItem>
              ))
            }
            </Menu>
          </div>


          <div>
            {/* Profile / language / logout entry-point now lives on
                the sidebar user card. The popover stays so the menu
                still works when the card opens it. */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseProfileMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={toggleColorMode}>
                {theme.mode === 'dark' ? i18n.t("mainDrawer.appBar.user.lightmode") : i18n.t("mainDrawer.appBar.user.darkmode")}
              </MenuItem>
              <NestedMenuItem
                label={i18n.t("mainDrawer.appBar.user.language")}
                parentMenuOpen={menuOpen}
              >
                {
                  Object.keys(messages).map((m) => (
                    <MenuItem key={m} onClick={() => handleChooseLanguage(m)}>
                      {messages[m].translations.mainDrawer.appBar.i18n.language}
                    </MenuItem>
                  ))
                }
              </NestedMenuItem>
              {user?.super === true && (
                <MenuItem
                  onClick={() => {
                    handleCloseProfileMenu();
                    history.push("/admin");
                  }}
                >
                  <VerifiedUserOutlinedIcon
                    fontSize="small"
                    // marginInlineEnd keeps the gap on the correct side
                    // under both LTR and RTL (Hebrew tenants flip the
                    // menu's reading direction; plain marginRight stuck
                    // the icon flush to the label in RTL).
                    style={{ marginInlineEnd: 8 }}
                  />
                  {i18n.t("mainDrawer.appBar.backToAdmin", {
                    defaultValue: "Back to admin",
                  })}
                </MenuItem>
              )}
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>

        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <PlatformBanner />
        {/* Wrap routed children so a soft fade + slight rise replays
            on every navigation — killed the abrupt instant-swap that
            made sidebar navigation feel "jerky".

            Key is derived from the route BASE, not the full pathname.
            Using the full pathname remounted everything inside when
            a param changed (e.g. /tickets → /tickets/<uuid> when the
            agent clicked a ticket), which destroyed local state in
            TicketsManagerTabs and dumped the user back onto the Open
            tab after every ticket click. The fade is meant for
            cross-page navigation, not for param tweaks within the
            same route. */}
        <div
          key={"/" + (location.pathname.split("/")[1] || "")}
          className={classes.routeFade}
          // Drop the GPU layer once the entrance animation finishes instead
          // of holding willChange for the life of the route — otherwise
          // every route mount keeps a compositor layer alive for no reason.
          onAnimationEnd={(e) => {
            e.currentTarget.style.willChange = "auto";
          }}
        >
          {children ? children : null}
        </div>
      </main>
      </div>
    </div>
    {/* Session-level in-call UI (Dialog portal) — follows the agent across
        ticket switches and other pages while a call is live. */}
    <SoftphoneCallModal />
    </SafeSoftphoneProvider>
    </SafeVoiceProvider>
  );
};

export default LoggedInLayout;
